import i18n from 'i18next';
import { initReactI18next, useTranslation, Trans, I18nextProvider } from 'react-i18next';

// Import locale files
import en from './locales/en.json';
import vi from './locales/vi.json';

// Check if running in test environment (window.railsEnv is set by Rails)
const isTestEnv = typeof window !== 'undefined' && window.railsEnv === 'test';

// Default configuration
const defaultConfig = {
  fallbackLng: 'vi',
  supportedLngs: ['en', 'vi'],
  interpolation: {
    escapeValue: false, // React already escapes by default
  },
  resources: {
    en: { translation: en },
    vi: { translation: vi },
  },
  // In test environment, throw exceptions on missing translations to catch errors early
  saveMissing: isTestEnv,
  missingKeyHandler: isTestEnv ? (lngs, ns, key, fallbackValue) => {
    throw new Error(`Missing translation key: "${key}" for languages: ${lngs.join(', ')}`);
  } : undefined,
};

/**
 * Initialize i18n with a specific locale
 * @param {string} locale - The locale to use ('en' or 'vi')
 * @param {Object} options - Additional i18next options
 * @returns {Object} - The initialized i18n instance
 */
export const initI18n = (locale = 'vi', options = {}) => {
  // Only initialize if not already initialized
  if (!i18n.isInitialized) {
    i18n
      .use(initReactI18next)
      .init({
        ...defaultConfig,
        lng: locale,
        ...options,
      });
  } else {
    // If already initialized, just change the language
    i18n.changeLanguage(locale);
  }

  return i18n;
};

/**
 * Create a new i18n instance for isolated use
 * Useful when you need multiple instances with different configurations
 * @param {string} locale - The locale to use ('en' or 'vi')
 * @param {Object} options - Additional i18next options
 * @returns {Object} - A new i18n instance
 */
export const createI18nInstance = (locale = 'vi', options = {}) => {
  const instance = i18n.createInstance();

  instance
    .use(initReactI18next)
    .init({
      ...defaultConfig,
      lng: locale,
      ...options,
    });

  return instance;
};

/**
 * Get the current locale
 * @returns {string} - The current locale
 */
export const getCurrentLocale = () => {
  return i18n.language || 'vi';
};

/**
 * Change the current locale
 * @param {string} locale - The locale to change to
 */
export const changeLocale = (locale) => {
  i18n.changeLanguage(locale);
};

/**
 * Format a number according to the current locale
 * @param {number} num - The number to format
 * @param {Object} options - Intl.NumberFormat options
 * @returns {string} - The formatted number
 */
export const formatNumber = (num, options = {}) => {
  const locale = getCurrentLocale() === 'vi' ? 'vi-VN' : 'en-US';
  return new Intl.NumberFormat(locale, options).format(num);
};

/**
 * Format a currency amount according to the current locale
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (default: VND)
 * @returns {string} - The formatted currency
 */
export const formatCurrency = (amount, currency = 'VND') => {
  const locale = getCurrentLocale() === 'vi' ? 'vi-VN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format a date according to the current locale
 * @param {Date|string} date - The date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - The formatted date
 */
export const formatDate = (date, options = {}) => {
  const locale = getCurrentLocale() === 'vi' ? 'vi-VN' : 'en-US';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Format a relative time (e.g., "2 days ago")
 * @param {Date|string} date - The date to format
 * @returns {string} - The formatted relative time
 */
export const formatRelativeTime = (date) => {
  const locale = getCurrentLocale() === 'vi' ? 'vi-VN' : 'en-US';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  } else if (diffInSeconds < 2592000) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  } else if (diffInSeconds < 31536000) {
    return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
  }
};

// Re-export react-i18next hooks and components
export { useTranslation, Trans, I18nextProvider };

// Export the i18n instance for advanced usage
export { i18n };

// Export I18nProvider component
export { default as I18nProvider } from './I18nProvider';

// Default export for convenience
export default {
  initI18n,
  createI18nInstance,
  getCurrentLocale,
  changeLocale,
  formatNumber,
  formatCurrency,
  formatDate,
  formatRelativeTime,
  useTranslation,
  Trans,
  I18nextProvider,
  i18n,
};
